<template>
  <div class="pc-orders">
    <BreadCrumb class="breadcrumb">
      <BreadCrumbItem to="/">
        Home
      </BreadCrumbItem>
      <BreadCrumbItem>Account</BreadCrumbItem>
      <BreadCrumbItem>Fulfillment Orders</BreadCrumbItem>
    </BreadCrumb>

    <!-- 订单状态菜单 -->
    <div
      v-if="loading"
      class="loading-wrapper"
    >
      <img
        :src="gifSrc"
        width="240px"
        alt=""
      >
    </div>
    <PageContainer
      v-if="!loading"
      class="order-list-wrapper"
    >
      <Tabs
        class="order-status-menu"
        v-model:active="currentIndex"
        @change="changeOrderStatus"
      >
        <Tab
          v-for="(status, index) in orderStatus"
          :key="index"
          :name="index"
          :title="status"
        />
      </Tabs>
      <!-- 空订单 -->
      <div
        v-if="showEmpty"
        class="empty-order-wrapper"
      >
        <img
          src="@/assets/H5/order/empty.png"
          width="240px"
          alt=""
        >
        <div class="text-1">
          Your fulfillment orders is empty
        </div>
        <div class="text-2">
          Add your purchase items in it.
        </div>
        <div
          class="back-home"
          @click="$router.push('/')"
        >
          Back Home
        </div>
      </div>

      <template v-else>
        <div class="order-table-header">
          <div class="order-table-header-item column-1">
            Products Details
          </div>
          <div class="order-table-header-item column-2">
            Quantity
          </div>
          <div class="order-table-header-item column-3">
            Order Amount
          </div>
          <div class="order-table-header-item column-3">
            Order State
          </div>
          <div class="order-table-header-item column-3">
            Action
          </div>
        </div>

        <!-- 订单列表 -->
        <div class="order-info-list">
          <div
            v-for="order in orderList"
            :key="order.b2bOrderVo.orderNo"
            class="order-info-list-item"
          >
            <div class="order-info-list-item-header">
              <div class="order-number">
                Order Number: {{ order.b2bOrderVo.orderNo }}
              </div>
              <div class="order-date">
                {{ order.b2bOrderVo.createTimeStr }}
              </div>
              <div class="line" />
              <div class="order-delivery-method">
                <img
                  src="@/assets/images/delivery.svg"
                  alt=""
                >
                Delivery Method: {{ deliveryText(order.b2bOrderVo.shipChannel) }}
              </div>
              <div
                v-if="order.b2bOrderVo.status === ORDER_STATUS.PAY_FAIL"
                class="error-info"
              >
                <img
                  src="@/assets/images/error-tips.svg"
                  alt=""
                >
                Payment confirmation failed, Please pay again.
              </div>
              <div
                v-if="order.b2bOrderVo.status === ORDER_STATUS.CANCELLED"
                class="warning-info"
              >
                <img
                  src="@/assets/images/icon-toast-warning.svg"
                  alt=""
                >
                The transaction is closed.
              </div>
            </div>
            <div class="order-info-list-item-body">
              <div
                class="details column-1"
                :class="{ singleItem: order.b2bOrderDetails.length === 1 }"
              >
                <div class="details-sku-list">
                  <!-- 订单内sku列表 -->
                  <div
                    v-for="sku in order.b2bOrderDetails"
                    :key="sku.skuId"
                    :class="{ singleItem: order.b2bOrderDetails.length === 1 }"
                    class="details-sku-list-item"
                  >
                    <Image
                      @click="toDetailPage(sku)"
                      class="sku-image"
                      :src="sku.skuImage"
                      alt=""
                    />
                    <div class="sku-info">
                      <div class="price">
                        <span>{{ order.b2bOrderVo.currencyCode.slice(0, 2) }}&nbsp;</span>
                        <span>{{ order.b2bOrderVo.currencyUnit }}</span>
                        <span>{{ sku.skuPrice }}</span>
                      </div>
                      <div class="desc">
                        {{ sku.spuName }}
                      </div>
                      <div class="sku-size-box">
                        <div class="sku-size">
                          <img
                            v-if="sku.skuImage"
                            :src="sku.skuImage"
                            alt=""
                          >
                          <img
                            v-else
                            src="@/assets/empty-image.svg"
                          >
                          <Tooltip :content="sku.skuSpec">
                            <div class="sku-spec">
                              {{ sku.skuSpec }}
                            </div>
                          </Tooltip>
                        </div>
                        <div class="sku-count">
                          ×{{ sku.skuQuantity }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-quantity column-2">
                {{ order.b2bOrderVo.productQuantity }}
              </div>
              <div class="col-amount column-3">
                <div class="total">
                  Total
                </div>
                <div class="price">
                  <IntlCurrency
                    :currency="order.b2bOrderVo.currencyCode"
                  />
                  {{ order.b2bOrderVo.totalAmount }}
                </div>
                <div class="line" />
                <div class="state">
                  {{ orderStatusText(order.b2bOrderVo.status) === 'Awaiting Payment' ? 'Pending Payment' : 'Actual Payment' }}
                </div>
                <div class="payment-price">
                  <IntlCurrency
                    currency="USD"
                  />
                  {{ orderStatusText(order.b2bOrderVo.status) === 'Awaiting Payment' ? order.b2bOrderVo.totalAmountUsd : order.b2bOrderVo.actualTotalAmountUsd }}
                </div>
              </div>
              <div class="col-state column-3">
                <div
                  class="state"
                  :class="`state--${order.b2bOrderVo.status}`"
                >
                  {{ orderStatusText(order.b2bOrderVo.status) }}
                </div>
                <div
                  v-if="order.b2bOrderVo.status === 0 || order.b2bOrderVo.status === 20 && order.b2bPaymentOrderVo?.method === 'BANK_TRANSFER_B2B'"
                  class="count-down-box"
                >
                  <div class="end-in">
                    End in
                  </div>
                  <Countdown
                    v-if="order.b2bOrderVo.status === 0"
                    :time="new Date(order.b2bOrderVo.createTime).getTime() + 24 * 60 * 60 * 1000"
                  />
                  <Countdown
                    v-if="order.b2bOrderVo.status === 20 && order.b2bPaymentOrderVo.method === 'BANK_TRANSFER_B2B'"
                    :time="new Date(order?.b2bPaymentOrderVo.bankTransferVO?.auditTime).getTime() + 24 * 60 * 60 * 1000"
                  />
                  <div class="warning-icon pointer">
                    <Tooltip
                      :offset="6"
                      content="If not paid within 24 hours, the order will be automatically canceled."
                    >
                      <img src="@/assets/images/explain.svg">
                    </Tooltip>
                  </div>
                </div>
                <div
                  v-if="order.b2bOrderLogisticsVos && order.b2bOrderLogisticsVos.length"
                  class="track-number-wrapper"
                >
                  <div class="text">
                    Tracking Number
                    <Tooltip
                      :offset="6"
                      content="Click to jump to the third-party platform to check the order logistics status."
                    >
                      <div style="width: 16px; height: 16px;">
                        <img
                          src="@/assets/images/explain.svg"
                          alt=""
                        >
                      </div>
                    </Tooltip>
                  </div>
                  <div
                    v-for="item in order.b2bOrderLogisticsVos"
                    :key="item.logisticsNo"
                    class="item"
                  >
                    <span
                      @click="toThirdPlatform(item.trackUrl)"
                      class="tracking-number"
                    >
                      {{ item.logisticsNo }}
                    </span>
                  </div>
                </div>
                <div
                  @click="handleViewDetail(order)"
                  class="view-details"
                >
                  View Details
                </div>
              </div>
              <div class="col-action column-3">
                <div
                  v-if="[ORDER_STATUS.PLACE_ORDER, ORDER_STATUS.PAY_FAIL].includes(order.b2bOrderVo.status) && order.b2bOrderVo.totalAmountUsd <= 500"
                  @mouseenter="handlpePaypalMouseenter(order)"
                  class="paypalButtonContainer"
                  ref="paypalButtonContainer"
                />
                <Button
                  v-if="[ORDER_STATUS.PLACE_ORDER, ORDER_STATUS.PAY_FAIL].includes(order.b2bOrderVo.status)"
                  @click="handleBankPayment(order)"
                  class="button"
                  type="primary"
                >
                  Bank Transfer
                </Button>
                <Button
                  v-if="order.b2bOrderVo.status === ORDER_STATUS.PAY_CONFIRMING"
                  @click="handleConfriming(order)"
                  class="button"
                  type="primary"
                  :disabled="order.b2bPaymentOrderVo?.method === 'PAYPAL_STANDARD_B2B'"
                >
                  Confirming
                </Button>
                <Button
                  @click="handleRebuy(order)"
                  class="button"
                  :type="[ORDER_STATUS.PLACE_ORDER, ORDER_STATUS.PAY_FAIL, ORDER_STATUS.PAY_CONFIRMING].includes(order.b2bOrderVo.status) ? 'default' : 'primary'"
                >
                  Repeat Order
                </Button>
                <Button
                  v-if="order.b2bOrderVo.status === ORDER_STATUS.PLACE_ORDER"
                  @click="showCancelDialog(order)"
                  class="button"
                  type="text"
                >
                  Cancel Order
                </Button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </PageContainer>

    <PageContainer>
      <Skeleton :visible="skeletonVisible" />
    </PageContainer>

    <Drawer
      v-model="drawers.orderDetail"
      title="Fulfillment Orders"
      :size="377"
    >
      <OrderDetail
        :data="currentOrder"
        @show-shipping-desc="showShippingDesc"
        @pay-now="handlePayNow"
      />
    </Drawer>

    <Drawer
      v-model="drawers.shippingDesc"
      title="Shipping Fee"
      :before-close="beforeCloseShipFee"
      :size="377"
    >
      <ShippingDesc />
    </Drawer>

    <Drawer
      v-model="drawers.payNow"
      title="Payment Methods"
      :size="377"
    >
      <PayNow :data="currentOrder" />
    </Drawer>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { fetchData } from '@/utils/fetch'
import { gaAnalytics, buriedPointUtil } from '@/utils/utils'
import { loadScript } from '@paypal/paypal-js'
import { usePlugin } from '@/hooks/usePlugin'
import BreadCrumb from '@/components/BreadCrumb/BreadCrumb.vue'
import BreadCrumbItem from '@/components/BreadCrumb/BreadCrumbItem.vue'
import Skeleton from './components/Skeleton.vue'
import Drawer from '@/components/Drawer/index.vue'
import OrderDetail from './components/OrderDetail.vue'
import PayNow from './components/PayNow.vue'
import ShippingDesc from '../H5Orders/components/ShippingDesc.vue'
import gifUrl from '@/assets/H5/empty.gif'
import Tooltip from '@/components/Tooltip/index.vue'
import { Image } from '@/components/Image'
import { useCartStore } from '@/pinia/cart'
import { PageContainer } from '@/components/PageLayout'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { Button } from '@/components/Button'
import { Tab, Tabs } from '@/components/Tab'
import { Countdown } from '@/components/Countdown'

const ORDER_STATUS = Object.freeze({
  PLACE_ORDER: 0, // 下单成功待支付
  PAY_CONFIRMING: 10, // 支付确认中
  PAY_FAIL: 20, // 支付失败
  PAY_COMPLETE: 30, // 支付完成
  IN_PROCESS: 40, // 采购中
  SHIPPED: 50, // 已发货
  DELIVERED: 90, // 已送达
  CANCELLED: 99 // 已取消
})

const { $confirm, $toast } = usePlugin()

const router = useRouter()
const route = useRoute()

const cartStore = useCartStore()

const orderStatus = ref(['All Orders', 'Awaiting Payment', 'Fulfillment', 'Completed'])
const currentIndex = ref(0)

const skeletonVisible = ref(false)

const searchInfo = ref({
  pageSize: 20,
  pageNow: 1,
  listStatusEnum: null
})

const noMore = ref(false)

const loading = ref(false)

const orderList = ref([])

const showEmpty = ref(false)

const currentOrder = ref(null)

const gifSrc = computed(() => {
  return gifUrl + '?' + new Date().getTime()
})

const changeOrderStatus = (index) => {
  if (index === 0) {
    searchInfo.value.listStatusEnum = null
  } else if (index === 1) {
    searchInfo.value.listStatusEnum = 'PENDING_PAYMENT'
  } else if (index === 2) {
    searchInfo.value.listStatusEnum = 'FULFILLING'
  } else if (index === 3) {
    searchInfo.value.listStatusEnum = 'COMPLETED'
  }
  getOrderList()
}

// 抽屉
const drawers = reactive({
  orderDetail: false,
  payNow: false,
  shippingDesc: false
})

const closeDrawers = () => {
  Object.keys(drawers).forEach(key => {
    drawers[key] = false
  })
}

const orderStatusText = (value) => {
  if ([0, 10, 20].includes(value)) {
    return 'Awaiting Payment'
  } else if ([30, 40, 50].includes(value)) {
    return 'Fulfillment'
  } else {
    return 'Completed'
  }
}

const deliveryText = (value) => {
  if (value === 0) return ''
  if (value === 1) return 'Shipping'
  if (value === 2) return 'Economy'
  if (value === 3) return 'Standard'
  if (value === 4) return 'Express'
}

const handleBankPayment = (order) => {
  gaAnalytics('p_pay_with_bank_click', 'p_pay_with_bank_click')
  buriedPointUtil({ eventId: 'POP207' })
  if (order.b2bOrderVo.status === ORDER_STATUS.PAY_FAIL) {
    router.push(`/bankPayment/${order.b2bOrderVo.orderNo}?type=repay`)
  } else {
    router.push(`/bankPayment/${order.b2bOrderVo.orderNo}`)
  }
}

const handleConfriming = (order) => {
  buriedPointUtil({ eventId: 'POP202' })
  gaAnalytics('p_confirming_click', 'p_confirming_click')
  if (order.b2bPaymentOrderVo?.method === 'PAYPAL_STANDARD_B2B') return

  const { href } = router.resolve({
    path: `/bankPayment/${order.b2bOrderVo.orderNo}?type=confirmed`
  })
  window.open(href, '_blank')
}

const handleRebuy = async (order) => {
  gaAnalytics('p_rebuy_click', 'p_rebuy_click')
  buriedPointUtil({ eventId: 'POP204' })

  const { code, data } = await fetchData('/appb/b2b/cart/reBuyOrder', {
    method: 'POST',
    body: JSON.stringify({
      orderNo: order.b2bOrderVo.orderNo
    })
  })
  if (code === 200) {
    fetchData('/appb/b2b/cart/cartCount').then(res => {
      cartStore.setCartCount(res.data)
    })

    sessionStorage.setItem('carts', JSON.stringify(data))
    localStorage.setItem('buy-again', true)
    router.push('/carts')
  }
}

const handleViewDetail = (order) => {
  currentOrder.value = order
  drawers.orderDetail = true
}

const showCancelDialog = (order) => {
  gaAnalytics('p_cancel_order_click', 'p_cancel_order_click')
  buriedPointUtil({ eventId: 'POP205' })

  currentOrder.value = order
  $confirm('Cancel Order?', 'Are you sure you want to cancel this order?').then(async () => {
    const { code } = await fetchData('/appb/b2b/order/cancelOrder/' + currentOrder.value.b2bOrderVo.orderNo, { method: 'PUT' })
    if (code === 200) {
      gaAnalytics('p_cancel_order_success', 'p_cancel_order_success')
      buriedPointUtil({ eventId: 'POP601' })

      $toast('Cancel successfully')
      getOrderList()
    }
  })
}

const beforeCloseShipFee = (done) => {
  done()
  handleViewDetail(currentOrder.value)
}

const showShippingDesc = () => {
  closeDrawers()
  drawers.shippingDesc = true
}

const handlePayNow = () => {
  closeDrawers()

  const totalAmountUsd = currentOrder.value.b2bOrderVo.totalAmountUsd
  if (totalAmountUsd >= 500) {
    router.push(`/bankPayment/${currentOrder.value.b2bOrderVo.orderNo}`)
  } else {
    drawers.payNow = true
  }
}

const getOrderList = async () => {
  orderList.value = []
  loading.value = true

  searchInfo.value.pageNow = 1
  noMore.value = false

  const { code, data } = await fetchData('/appb/b2b/order/page', {
    method: 'POST',
    body: JSON.stringify({
      ...searchInfo.value
    }),
    failedRedirect: true
  })

  if (code === 200) {
    const rows = data.rows || []
    orderList.value = rows

    if (rows.length < 20) {
      noMore.value = true
    }
    if (orderList.value.length === 0 && currentIndex.value === 0) {
      showEmpty.value = true
    }

    setTimeout(() => {
      initPaypal()
    }, 0)
  }
  loading.value = false
}

const loadMore = async (e) => {
  const { scrollTop, clientHeight, scrollHeight } = document.documentElement

  // 避免初始化时立即触发
  if (searchInfo.value.pageNow === 1 && orderList.value.length === 0) return

  if (scrollTop + clientHeight >= scrollHeight - 10) {
    if (noMore.value) return

    if (skeletonVisible.value) return
    skeletonVisible.value = true

    searchInfo.value.pageNow++
    const { code, data } = await fetchData('/appb/b2b/order/page', {
      method: 'POST',
      body: JSON.stringify({
        ...searchInfo.value
      })
    })

    if (code === 200) {
      const rows = data.rows || []
      orderList.value = [...orderList.value, ...rows]

      if (rows.length < 20) {
        noMore.value = true
      }

      initPaypal()
    }
    skeletonVisible.value = false
  }
}

const paypalOrderObject = ref(null)
const handlpePaypalMouseenter = (order) => {
  paypalOrderObject.value = order
}

const paypalButtonContainer = ref(null)
const tradeCode = ref(null)
const initPaypal = async () => {
  const { data } = await fetchData('/appb/b2b/pay/getPaypalClientId')
  try {
    const paypal = await loadScript({
      'client-id': data,
      intent: 'capture',
      components: 'buttons',
      disableFunding: 'paylater'
    })

    // 按钮配置
    const buttonConfig = {
      style: {
        layout: 'vertical', // 设置按钮水平排列
        shape: 'rect', // 圆形按钮
        size: 'responsive', // 自适应大小
        tagline: false, // 隐藏标签
        height: 30
      },
      createOrder: async () => { // 创建订单，返回一个订单 ID，供 PayPal 处理
        gaAnalytics('p_paypal_click', 'p_paypal_click')
        buriedPointUtil({ eventId: 'POP206' })
        const { code, data } = await fetchData('/appb/b2b/order/createOrderPay', {
          method: 'POST',
          body: JSON.stringify({
            orderNo: paypalOrderObject.value.b2bOrderVo.orderNo,
            paymentMethodCode: 'PAYPAL_STANDARD_B2B',
            transactionNo: ''
          })
        })

        if (code === 200) {
          tradeCode.value = data.tradeCode
          return data.thirdTradeCode
        }
      },
      onApprove: async (data) => {
        const { code } = await fetchData('/appb/b2b/order/completePayAction', {
          method: 'POST',
          body: JSON.stringify({
            tradeCode: tradeCode.value,
            orderNo: paypalOrderObject.value.b2bOrderVo.orderNo
          })
        })

        if (code === 200) {
          gaAnalytics('p_pay_success', 'p_pay_success')
          localStorage.setItem('ga_P_Pay_Success', true)
          router.go(0)
        }
      },
      onCancel: (data) => {
        console.log('用户取消支付', data)
      },
      onError: (err) => {
        console.error('支付失败', err)
      }
    }

    if (paypalButtonContainer.value) {
      paypalButtonContainer.value.forEach(item => {
        paypal.Buttons(buttonConfig).render(item)
      })
    }
  } catch (error) {
    console.error('PayPal初始化失败:', error)
    setTimeout(() => {
      initPaypal() // 如果加载失败,3秒后重试
    }, 3000)
  }
}

const handleFromEmail = async () => {
  if (route.fullPath.includes('fromEmail')) {
    const orderNo = route.query.orderNo
    const { data } = await fetchData('/appb/b2b/order/page', {
      method: 'POST',
      body: JSON.stringify({
        orderNo
      })
    })
    const order = data.rows[0]
    currentOrder.value = order

    const totalAmountUsd = order.b2bOrderVo.totalAmountUsd
    if (totalAmountUsd >= 200) {
      router.push(`/bankPayment/${orderNo}?type=repay`)
    } else {
      drawers.payNow = true
    }
  }
}

const toDetailPage = (sku) => {
  const offerId = sku.spuId
  const { href } = router.resolve({
    path: '/offer/' + offerId
  })
  window.open(href, '_blank')
}

const toThirdPlatform = (url) => {
  window.open(url, '_blank')
}

onMounted(() => {
  gaAnalytics('p_order_page_view', 'p_order_page_view')
  buriedPointUtil({ eventId: 'POP501' })
  if (route.query.status) {
    changeOrderStatus(+route.query.status)
  } else {
    getOrderList()
  }
  handleFromEmail()

  window.addEventListener('scroll', loadMore)

  if (localStorage.getItem('ga_P_Pay_Success')) {
    gaAnalytics('P_Pay_Success', 'P_Pay_Success')
    localStorage.removeItem('ga_P_Pay_Success')
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', loadMore)
})
</script>

<style lang="less" scoped>
.pc-orders {
  .pointer {
    cursor: pointer;
  }

  .empty-order-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 220px - 128px - 48px);

    .text-1 {
      color: #232323;
      font-size: 16px;
      font-family: mon-600;
      line-height: 24px;
    }

    .text-2 {
      color: #232323;
      font-size: 14px;
      font-family: mon-500;
      line-height: 18px;
    }

    .back-home {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 16px;
      height: 40px;
      border-radius: 20px;
      border: 1px solid var(--main-border-color);
      color: #232323;
      font-size: 14px;
      font-family: mon-600;
      cursor: pointer;
      padding: 0 40px;
    }
  }

  .loading-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 220.5px - 64px - 48px);
  }

  .order-list-wrapper {
    padding: 24px 0;
    min-height: calc(100vh - 220px - 64px - 72px);
  }

  .order-status-menu {

    :deep(.tabs-nav) {
      gap: 14px;
    }

    :deep(.tabs-nav__item) {
      padding: 0 12px 12px;
    }

    :deep(.tabs-nav__line) {
      width: 40px;
    }

    :deep(.tabs-content) {
      padding: 0;
    }
  }

  .column-1 {
    width: 584px;
  }

  .column-2 {
    width: 124px;
  }

  .column-3 {
    flex-shrink: 0;
    width: 164px;
  }

  .order-table-header {
    display: flex;
    height: 40px;
    padding: 12px 0;
    margin: 16px auto;
    background-color: #f8f8f8;
    border-radius: 4px;
    .order-table-header-item {
      display: flex;
      align-items: center;
      justify-content: center;
      border-right: 1px solid var(--dark-split-color);
      font-size: 12px;
      color: #232323;
      font-family: mon-500;

      &:last-child {
        border-right: none;
      }
    }
  }

  .order-info-list {
    .order-info-list-item {
      height: 296px;
      border: 1px solid var(--main-border-color);
      border-radius: 4px;
      margin-bottom: 16px;
      &:last-child {
        margin-bottom: 0;
      }

      .order-info-list-item-header {
        position: relative;
        display: flex;
        align-items: center;
        height: 40px;
        background-color: #f8f8f8;
        padding: 12px 24px;

        .order-number {
          color: #232323;
          font-size: 12px;
          font-family: mon-500;
          margin-right: 8px;
        }

        .order-date {
          color: #6e717a;
          font-size: 12px;
          font-family: mon-500;
          margin-right: 8px;
        }

        .line {
          height: 12px;
          width: 0.5px;
          background-color: var(--dark-split-color);
          margin-right: 8px;
        }

        .order-delivery-method {
          display: flex;
          align-items: center;
          color: #529426;
          font-size: 12px;
          font-family: mon-500;
          line-height: 16px;
          img {
            margin-right: 4px;
          }
        }

        .warning-info {
          display: flex;
          align-items: center;
          position: absolute;
          right: 24px;
          color: #5C2F04;
          font-size: 12px;
          font-family: mon-500;
          &.grey {
            color: #b2b7c2;
          }
          img {
            margin-right: 4px;
            width: 16px;
            height: 16px;
          }
        }

        .error-info {
          display: flex;
          align-items: center;
          position: absolute;
          right: 24px;
          color: #f54550;
          font-size: 12px;
          font-family: mon-500;
          &.grey {
            color: #b2b7c2;
          }
          img {
            margin-right: 4px;
          }
        }
      }

      .order-info-list-item-body {
        display: flex;

        .details {
          height: 255px;
          padding-left: 24px;
          border-right: 1px solid var(--light-split-color);
          overflow: auto;
          position: relative;

          &.singleItem {
            display: flex;
            align-items: center;
          }

          .details-sku-list {
            position: relative;
            .details-sku-list-item {
              display: flex;
              gap: 12px;
              height: 128px;
              padding: 16px 0;
              border-bottom: 1px solid var(--light-split-color);

              &.singleItem {
                border-bottom: none;
              }

              .sku-image {
                flex-shrink: 0;
                width: 96px;
                height: 96px;
                border-radius: 4px;
                cursor: pointer;
                object-fit: contain;
              }

              .sku-info {
                width: 100%;
                .price {
                  display: flex;
                  color: #232323;
                  font-family: mon-600;
                  font-size: 12px;
                }
                .desc {
                  width: 404px;
                  margin-top: 4px;
                  color: #5c5c5c;
                  font-size: 12px;
                  font-family: mon-500;
                  line-height: 16px;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                .sku-size-box {
                  position: relative;
                  display: flex;
                  gap: 8px;
                  margin-top: 12px;
                  .sku-size {
                    display: flex;
                    align-items: center;
                    width: 200px;
                    height: 24px;
                    border-radius: 2px;
                    background-color: #f8f8f8;
                    padding: 0 8px 0 4px;
                    cursor: pointer;

                    img {
                      width: 16px;
                      height: 16px;
                      margin-right: 6px;
                      border-radius: 1px;
                    }
                    .sku-spec {
                      width: 160px;
                      color: #6e717a;
                      font-size: 12px;
                      font-family: mon-500;
                      white-space: nowrap;
                      text-overflow: ellipsis;
                      overflow: hidden;
                    }
                  }
                  .sku-count {
                    display: flex;
                    align-items: center;
                    color: #5c5c5c;
                    font-size: 12px;
                    font-family: mon-500;
                    line-height: 16px;
                  }
                }
              }
            }
          }
        }

        .col-quantity {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #232323;
          font-size: 12px;
          font-family: mon-500;
          border-right: 1px solid var(--light-split-color);
        }

        .col-amount {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-right: 0.5px solid #E4E6EB;

          .total {
            color: #5c5c5c;
            font-size: 14px;
            font-family: mon-500;
            line-height: 18px;
          }

          .price {
            margin-top: 4px;
            display: flex;
            align-items: center;
            color: #232323;
            font-size: 16px;
            font-family: mon-700;
            line-height: 20px;
          }

          .line {
            width: 132px;
            height: 0.5px;
            background-color: #e4e6eb;
            margin: 16px 0;
          }

          .state {
            color: #5c5c5c;
            font-size: 14px;
            font-family: mon-500;
            line-height: 18px;
          }

          .payment-price {
            margin-top: 4px;
            display: flex;
            align-items: center;
            color: #052784;
            font-size: 16px;
            font-family: mon-700;
            line-height: 20px;
          }
        }

        .col-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-right: 0.5px solid #E4E6EB;

          .state {
            color: #232323;
            font-size: 14px;
            font-family: mon-500;
            line-height: 18px;

            &--20 {
              color: #7A0505;
            }

            &--99 {
              color: #5C2F04;
            }
          }

          .count-down-box {
            position: relative;
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 16px;

            .end-in {
              color: #5c5c5c;
              font-size: 12px;
              font-family: mon-500;
            }

            :deep(.countdown) {
              gap: 4px;
            }

            .warning-icon {
              width: 16px;
              height: 16px;
              margin-left: 4px;
            }
          }

          .track-number-wrapper {
            position: relative;
            text-align: center;
            margin-top: 12px;
            .text {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 12px;
              color: #5c5c5c;
              font-family: 500;
              margin-bottom: 2px;
            }
            .tracking-number {
              font-size: 11px;
              color: #232323;
              line-height: 16px;
              text-decoration: underline;
              cursor: pointer;
              &:hover {
                color: var(--link-hover-color);
              }
            }
          }

          .view-details {
            margin-top: 24px;
            color: #007EB3;
            font-size: 14px;
            font-family: mon-500;
            line-height: 18px;
            text-decoration: underline;

            &:hover {
              color: var(--link-hover-color);
              text-decoration: underline;
              cursor: pointer;
            }
          }
        }

        .col-action {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;

          .paypalButtonContainer {
            width: 132px;
            height: 70PX;
            overflow: hidden;
          }

          .button {
            border-radius: 4px;
            height: 32px;
            width: 132px;
            font-size: 12px;
          }
        }
      }
    }
  }
}
</style>
